import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppProvider } from "@/context/AppContext";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import ImportStatement from "@/pages/ImportStatement";
import Accounts from "@/pages/Accounts";
import Recurring from "@/pages/Recurring";
import Statement from "@/pages/Statement";
import PublicStatement from "@/pages/PublicStatement";
import PublicProfile from "@/pages/PublicProfile";
import Reports from "@/pages/Reports";
import CompanyProfile from "@/pages/CompanyProfile";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import Enquiries from "@/pages/Enquiries";
import Documents from "@/pages/Documents";
import BankStatements from "@/pages/BankStatements";
import { Loader2 } from "lucide-react";

function Protected({ children, perm, superAdmin }) {
  const { user, loading, hasPermission } = useAuth();
  if (loading || user === null)
    return <div className="min-h-screen grid place-items-center"><Loader2 className="animate-spin text-indigo-600" size={32} /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (superAdmin && user.role !== "super_admin") return <Navigate to="/dashboard" replace />;
  if (perm && !hasPermission(perm)) return <Navigate to="/dashboard" replace />;
  return (
    <AppProvider>
      <Layout>{children}</Layout>
    </AppProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/s/:token" element={<PublicStatement />} />
      <Route path="/p/:companyId" element={<PublicProfile />} />
      <Route path="/dashboard" element={<Protected perm="view_dashboard"><Dashboard /></Protected>} />
      <Route path="/transactions" element={<Protected perm="view_dashboard"><Transactions /></Protected>} />
      <Route path="/import" element={<Protected perm="import_statements"><ImportStatement /></Protected>} />
      <Route path="/accounts" element={<Protected perm="view_dashboard"><Accounts /></Protected>} />
      <Route path="/recurring" element={<Protected perm="view_dashboard"><Recurring /></Protected>} />
      <Route path="/reports" element={<Protected perm="view_reports"><Reports /></Protected>} />
      <Route path="/statement" element={<Protected perm="view_reports"><Statement /></Protected>} />
      <Route path="/company" element={<Protected perm="view_dashboard"><CompanyProfile /></Protected>} />
      <Route path="/enquiries" element={<Protected perm="view_dashboard"><Enquiries /></Protected>} />
      <Route path="/documents" element={<Protected perm="view_dashboard"><Documents /></Protected>} />
      <Route path="/bank-statements" element={<Protected perm="view_dashboard"><BankStatements /></Protected>} />
      <Route path="/users" element={<Protected superAdmin><Users /></Protected>} />
      <Route path="/settings" element={<Protected perm="view_dashboard"><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
